import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import {
  ArrowRightIcon,
  DownloadIcon,
  ImagePlaceholderIcon,
  MessageTickDoubleIcon,
  MessageTickSingleIcon,
  SmsFailedIcon,
} from '#/shared/ui/icons'
import { LinkifiedText } from '#/shared/ui/linkified-text'
import { Popover } from '#/shared/ui/popover'
import { Skeleton } from '#/shared/ui/skeleton'

import type {
  ChatPhoto,
  OpenImageHandler,
  TimelineItem,
} from '../../model/conversation'
import { AiTag } from './ai-tag'

type MessageItem = Extract<TimelineItem, { kind: 'message' }>

/** WhatsApp-style delivery check on outgoing bubbles: ✓ sent, ✓✓ delivered.
 *  Sending shows no check yet; failed renders its notice below, never a check. */
function DeliveryTick({ status }: { status: MessageItem['status'] }) {
  if (status === 'sending') return null
  if (status === 'sent')
    return <MessageTickSingleIcon className="size-3 opacity-85" />
  // delivered, or a legacy item with no known status, shows the double check.
  return <MessageTickDoubleIcon className="size-3 opacity-85" />
}

/** The meta's contents — AI chip, time, and (for outgoing) a delivery check.
 *  Rendered twice: once invisibly inline to reserve space on the text's last
 *  line, and once visibly pinned to the bubble's bottom-right corner. */
function MetaContent({
  message,
  outgoing,
}: {
  message: MessageItem
  outgoing: boolean
}) {
  return (
    <>
      {message.ai && <AiTag onAccent={outgoing} />}
      <span className="text-2xs leading-none whitespace-nowrap">
        {message.meta}
      </span>
      {/* Portal chat has no carrier delivery report, so it shows no check. */}
      {outgoing && !message.chat && <DeliveryTick status={message.status} />}
    </>
  )
}

/** Invisible copy of the meta placed at the end of the text — it reserves the
 *  exact width the real meta needs so the last line wraps a gap for it (and
 *  drops the meta onto its own line only when the text genuinely fills the row). */
function MetaSpacer({
  message,
  outgoing,
}: {
  message: MessageItem
  outgoing: boolean
}) {
  return (
    <span
      aria-hidden
      className="ml-1.5 inline-flex items-center gap-1 align-bottom invisible select-none"
    >
      <MetaContent message={message} outgoing={outgoing} />
    </span>
  )
}

/** The visible meta, pinned to the bubble's bottom-right corner over the gap the
 *  spacer reserved. On the blue outgoing bubble it flips to translucent white;
 *  incoming stays muted gray. */
function BubbleMeta({
  message,
  outgoing,
}: {
  message: MessageItem
  outgoing: boolean
}) {
  return (
    <span
      className={cn(
        'absolute right-3 bottom-2 flex items-center gap-1',
        outgoing ? 'text-paper/80' : 'text-warm-500',
      )}
    >
      <MetaContent message={message} outgoing={outgoing} />
    </span>
  )
}

/** The text bubble with its inline meta; omitted for media-only messages. When
 *  `onToggleRoute` is set, tapping the bubble reveals its sender→recipient line. */
function MessageText({
  message,
  outgoing,
  onToggleRoute,
}: {
  message: MessageItem
  outgoing: boolean
  onToggleRoute?: () => void
}) {
  // Filled bubble: blue for my messages, an elevated light surface for the
  // other side. The bottom tail corner (right for outgoing, left for incoming)
  // tightens to a small 4px radius, giving each side its tail per the design.
  // `relative` anchors the corner-pinned meta; `overflow-hidden` clips the
  // rounded corners.
  // In-app portal chat swaps the SMS palette for green on both sides, so the
  // agent can tell at a glance which messages never went out over the carrier.
  const className = cn(
    'relative overflow-hidden rounded-tl-2xl rounded-tr-2xl px-3 py-2 text-left',
    outgoing
      ? cn(
          'rounded-bl-2xl rounded-br-sm text-paper',
          message.chat ? 'bg-chat-portal' : 'bg-blue-500',
        )
      : cn(
          'rounded-br-2xl rounded-bl-sm text-gray-900',
          message.chat ? 'bg-chat-portal-incoming' : 'bg-chat-incoming',
        ),
  )
  // An invisible spacer reserves the meta's width at the end of the text so the
  // last line leaves a gap for it; the real meta is pinned to the bottom-right
  // corner over that gap, dropping to its own line only when the text fills the
  // row — without the float/translate hack that could clip it.
  const showMeta = message.status !== 'failed'
  // pre-wrap keeps the body verbatim — the newlines and space runs the SMS
  // actually carries — instead of letting HTML collapse them to single spaces.
  // Links inherit the bubble's own text color (white on filled, gray on
  // incoming) — the underline alone marks them; a blue link would vanish on
  // the blue outgoing bubble.
  const content = (
    <span className="text-sm leading-5 whitespace-pre-wrap wrap-anywhere">
      <LinkifiedText text={message.text} />
      {showMeta && <MetaSpacer message={message} outgoing={outgoing} />}
      {showMeta && <BubbleMeta message={message} outgoing={outgoing} />}
    </span>
  )
  if (!onToggleRoute)
    return <div className={cn(className, 'select-text')}>{content}</div>
  // A plain div (not a button) so the message text stays freely selectable and
  // shows a text caret rather than a pointer — tapping still toggles the route,
  // but only when the user isn't dragging out a selection.
  return (
    <div
      onClick={() => {
        // Don't toggle when the user is dragging to select/copy the text.
        if (!window.getSelection()?.isCollapsed) return
        onToggleRoute()
      }}
      className={cn(className, 'cursor-text select-text')}
    >
      {content}
    </div>
  )
}

function MessagePhoto({
  photo,
  sizeClass,
  showCaption,
  onOpen,
}: {
  photo: ChatPhoto
  /** Tailwind size classes for the tile (fixed for single, square in a grid). */
  sizeClass: string
  /** Hide the filename strip on compact grid tiles. */
  showCaption: boolean
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)
  const [errored, setErrored] = useState(false)
  const ready = loaded || errored
  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-xl bg-gray-200',
        sizeClass,
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('phonePanel.conversation.viewPhoto')}
        className="block size-full cursor-pointer transition group-hover:brightness-95"
      >
        {!loaded && !errored && (
          <Skeleton className="absolute inset-0 rounded-none" />
        )}
        {errored ? (
          <ImagePlaceholderIcon className="size-full" />
        ) : (
          <img
            src={photo.url}
            alt={photo.name}
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            className={cn(
              'size-full object-cover transition-opacity duration-200',
              loaded ? 'opacity-100' : 'opacity-0',
            )}
          />
        )}
      </button>
      {ready && showCaption && (
        <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center bg-linear-to-t from-black/65 to-transparent px-2 pt-5 pb-1.5">
          <span className="truncate text-2xs font-medium text-paper drop-shadow-sm">
            {photo.name}
          </span>
        </span>
      )}
      {ready && !errored && (
        <a
          href={photo.url}
          download={photo.name}
          target="_blank"
          rel="noreferrer"
          aria-label={t('phonePanel.conversation.downloadPhoto')}
          className="absolute top-1.5 right-1.5 flex size-7 items-center justify-center rounded-full bg-black/45 text-paper opacity-0 backdrop-blur-sm transition-opacity duration-150 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-black/60"
        >
          <DownloadIcon className="size-3.5" />
        </a>
      )}
    </div>
  )
}

function MessagePhotos({
  photos,
  outgoing,
  onOpenImage,
}: {
  photos: ReadonlyArray<ChatPhoto>
  outgoing: boolean
  onOpenImage: OpenImageHandler
}) {
  // A single photo keeps its larger tile; multiples collapse into a compact
  // two-column grid of square thumbnails that opens the gallery on tap.
  if (photos.length === 1) {
    return (
      <MessagePhoto
        photo={photos[0]}
        sizeClass={outgoing ? 'h-35 w-52' : 'h-26 w-35'}
        showCaption
        onOpen={() => onOpenImage(photos, 0)}
      />
    )
  }
  return (
    <div className={cn('grid grid-cols-2 gap-1', outgoing ? 'w-52' : 'w-44')}>
      {photos.map((photo, index) => (
        <MessagePhoto
          key={photo.url}
          photo={photo}
          sizeClass="aspect-square w-full"
          showCaption={false}
          onOpen={() => onOpenImage(photos, index)}
        />
      ))}
    </div>
  )
}

/** The red "!" marker to the right of a failed bubble. Clicking it opens a
 *  panel with the full failure reason. */
function FailedMarker({ detail }: { detail?: string }) {
  const { t } = useTranslation()
  const text = detail ?? t('phonePanel.conversation.sendError')
  return (
    <Popover
      portal
      side="top"
      align="center"
      matchTriggerWidth={false}
      contentClassName="w-64 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-popover"
      trigger={() => (
        <button
          type="button"
          aria-label={t('phonePanel.conversation.failureDetails')}
          className="flex shrink-0 cursor-pointer items-center justify-center text-red-400 transition-transform hover:scale-110"
        >
          <SmsFailedIcon className="size-4.5" />
        </button>
      )}
    >
      {() => (
        <p className="text-xs leading-snug whitespace-pre-wrap text-gray-700 wrap-anywhere">
          {text}
        </p>
      )}
    </Popover>
  )
}

/** Meta row beneath the bubble — only for media-only or failed messages, where
 *  the timestamp can't ride inside a text bubble. The failure reason itself
 *  lives solely in the "!" marker's popover, so a failed message shows the
 *  bare time here; a media-only message sent on the shared mainline shows the
 *  photos' full sender + time caption (DEV-700). */
function OuterMeta({ message }: { message: MessageItem }) {
  const caption =
    message.mainline && !message.text ? message.photos?.[0]?.caption : undefined
  return (
    <div className="flex items-center gap-1.5 px-1">
      {message.ai && <AiTag />}
      <span className="truncate text-2xs text-warm-500">
        {caption ?? message.meta}
      </span>
    </div>
  )
}

/** Photos, text bubble, and any outer meta — the bubble column's contents. */
function BubbleStack({
  message,
  outgoing,
  onOpenImage,
  onToggleRoute,
}: {
  message: MessageItem
  outgoing: boolean
  onOpenImage: OpenImageHandler
  onToggleRoute?: () => void
}) {
  // The timestamp lives inside text bubbles; media-only and failed messages
  // can't host it there, so they fall back to a row beneath the bubble.
  const failed = message.status === 'failed'
  const showOuterMeta = failed || (!!message.photos && !message.text)
  const bubble = (
    <>
      {message.photos ? (
        <MessagePhotos
          photos={message.photos}
          outgoing={outgoing}
          onOpenImage={onOpenImage}
        />
      ) : null}
      {message.text ? (
        <MessageText
          message={message}
          outgoing={outgoing}
          onToggleRoute={onToggleRoute}
        />
      ) : null}
    </>
  )
  if (failed) {
    // iMessage failed layout: the red "!" marker sits to the right of the
    // outgoing bubble, vertically centered against it; the reason line sits
    // right-aligned under the bubble (the pr-6 offsets the marker's width so
    // it lines up with the bubble's edge, not the marker's).
    return (
      <>
        <div className="flex items-center gap-1.5">
          <div className="flex min-w-0 flex-col items-end gap-0.75">
            {bubble}
          </div>
          <FailedMarker detail={message.errorDetail} />
        </div>
        <div className="max-w-full pr-6">
          <OuterMeta message={message} />
        </div>
      </>
    )
  }
  return (
    <>
      {bubble}
      {showOuterMeta && <OuterMeta message={message} />}
    </>
  )
}

/** System line under an inbound bubble whose reply matched a Texting
 *  Automation status rule, e.g. "Deal archived automatically." */
function AutomationNote({ note }: { note: string }) {
  return (
    <span className="flex items-start gap-1 px-1 text-xs text-warm-500 wrap-anywhere">
      <ArrowRightIcon className="mt-0.5 size-3 shrink-0" />
      {note}
    </span>
  )
}

/** Tap-to-reveal state for a bubble's sender→recipient line. Returns the tap
 *  handler (absent when there's no route to show) and the line when open. */
function useRouteReveal(message: MessageItem) {
  const [open, setOpen] = useState(false)
  const canToggle =
    !!message.route && !!message.text && message.status !== 'failed'
  return {
    onToggle: canToggle ? () => setOpen((prev) => !prev) : undefined,
    line: open ? message.route : undefined,
  }
}

/** The bubble's one-shot animation: the flash wins when a message is jumped to
 *  from a notification, otherwise the entrance for a freshly arrived message.
 *  Only one runs at a time — both drive the CSS `animation` property and would
 *  collide if applied together. */
function bubbleAnimationClass(
  entered: boolean,
  highlighted: boolean,
  outgoing: boolean,
): string | false {
  if (highlighted) return 'animate-highlight-flash'
  if (!entered) return false
  return cn(
    'animate-message-in',
    outgoing ? 'origin-bottom-right' : 'origin-bottom-left',
  )
}

export function MessageBubble({
  message,
  onOpenImage,
  animate = false,
  highlighted = false,
}: {
  message: MessageItem
  onOpenImage: OpenImageHandler
  /** Play the entrance animation — true only for freshly arrived messages. */
  animate?: boolean
  /** Flash the bubble — true for a message jumped to from a notification. */
  highlighted?: boolean
}) {
  const outgoing = message.direction === 'out'
  // Capture at mount so a later re-render can't restart or cut the animation.
  const [entered] = useState(animate)
  // Tap a text bubble to reveal its sender→recipient line beneath.
  const { onToggle, line } = useRouteReveal(message)
  return (
    <div
      data-message-id={message.id}
      className={cn('flex w-full', outgoing ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'flex max-w-72 flex-col gap-0.75',
          outgoing ? 'items-end' : 'items-start',
          bubbleAnimationClass(entered, highlighted, outgoing),
        )}
      >
        <BubbleStack
          message={message}
          outgoing={outgoing}
          onOpenImage={onOpenImage}
          onToggleRoute={onToggle}
        />
        {message.automationNote && (
          <AutomationNote note={message.automationNote} />
        )}
        {line && (
          <span className="animate-slide-down px-1 text-xs whitespace-nowrap text-warm-500">
            {line}
          </span>
        )}
      </div>
    </div>
  )
}
